import * as React from "react";
import { useReviews } from "@/hooks/useReviews";
import { StoredResponseRequest } from "@/types/review.types";
import { useTileSearch } from "@/hooks/useTileSearch";
import { Impact, ReviewTileItem } from "@/types/tile.types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,

} from "@/taskpane/components/ui/select";
import { Loader, X } from "lucide-react";
import { toast } from "sonner";
import TilesBulkActions from "./TilesBulkActions";

const TileItem = React.lazy(() => import("./TileItem"));

interface TilesListProps {
  selectedQueryTerms: string[];
  documentId: string;
}

const impactOptions: { value: Exclude<Impact, "ALL">; label: string }[] = [
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

export const TilesList: React.FC<TilesListProps> = ({ selectedQueryTerms, documentId }) => {
  const [impactFilter, setImpactFilter] = React.useState<Impact>("ALL");
  const [isBulkOperationInProgress, setIsBulkOperationInProgress] = React.useState(false);

  // Create request object for useReviews hook
  const reviewRequest: StoredResponseRequest | null = React.useMemo(() => {
    if (selectedQueryTerms.length === 0) return null;
    const request = {
      document_id: [documentId], // StoredResponseRequest expects string[]
      query_term: selectedQueryTerms, // StoredResponseRequest expects string[]
    };
    return request;
  }, [selectedQueryTerms, documentId]);

  const { data: reviewsData } = useReviews(reviewRequest);
  const searchHook = useTileSearch({
    onError: (error) => toast.error(error),
    highlightInDocument: true,
    caseSensitive: false,
    wholeWords: false,
  });

  const selectedImpactCount = React.useMemo(() => {
    if (!reviewsData?.impact_counts) return 0;
    if (impactFilter === "ALL") {
      return (
        reviewsData.impact_counts.HIGH +
        reviewsData.impact_counts.MEDIUM +
        reviewsData.impact_counts.LOW
      );
    }
    return reviewsData.impact_counts[impactFilter];
  }, [reviewsData?.impact_counts, impactFilter]);

  // Convert API response to ReviewTileItem format with formatted content
  const items = React.useMemo(() => {
    if (!reviewsData?.response) return [];

    const reviewItems: ReviewTileItem[] = [];
    Object.entries(reviewsData.response).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item: any, index: number) => {
          // Convert API response key to query format exactly like Vue's formatTile()
          let formattedQuery: string;
          if (key.startsWith("compare_precedent_")) {
            formattedQuery = key.replace("compare_precedent_", "").replace(/_/g, "-");
          } else {
            // Handle special cases matching Vue useFormattedTiles exactly
            switch (key) {
              case "negotiationPoints":
                formattedQuery = "points-to-negotiate";
                break;
              case "placeholders":
                formattedQuery = "find-placeholders";
                break;
              case "riskPoints":
                formattedQuery = "risk-analysis";
                break;
              default:
                formattedQuery = key.replace(/([A-Z])/g, "-$1").toLowerCase();
            }
          }

          // For display: convert dashes to spaces like Vue does (item?.query?.replace(/-/g, ' '))
          const displayType = formattedQuery.startsWith("compare-precedent-")
            ? formattedQuery.replace("compare-precedent-", "").replace(/-/g, " ")
            : formattedQuery.replace(/-/g, " ");

          reviewItems.push({
            id: `${key}-${index}`,
            clause: item.clause || key,
            section: item.section,
            reason: item.reason,
            analysis: item.analysis,
            explanation: item.explanation,
            impact: item.impact as Impact,
            queryType: displayType,
            query: formattedQuery, // Store the formatted query like Vue does
            documentName: item.documentName,
            originalData: item,
            originalKey: key,
          });
        });
      }
    });
    return reviewItems;
  }, [reviewsData]);

  const filteredItems = React.useMemo(() => {
    if (impactFilter === "ALL") return items;
    return items.filter((item) => item.impact === impactFilter);
  }, [items, impactFilter]);

  // Handle tile click to search in document
  const handleTileClick = async (item: ReviewTileItem) => {
    await searchHook.searchInDocumentWithTile(item);
  };

  return (
    <div className="flex flex-col gap-4 w-full flex-1 min-h-0">
      {items.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Select
              onValueChange={(value) => setImpactFilter(value as Impact)}
              value={impactFilter === "ALL" ? undefined : impactFilter}
            >
              <SelectTrigger className="w-[130px] px-3 text-sm font-medium">
                <div className="flex items-center justify-between w-full">
                  <span>
                    {impactFilter === "ALL"
                      ? "Impact"
                      : impactOptions.find((opt) => opt.value === impactFilter)?.label}
                  </span>
                  {impactFilter !== "ALL" && (
                    <div
                      role="button"
                      tabIndex={0}
                      className="ml-2 hover:bg-slate-100 rounded-full p-0.5 z-50"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImpactFilter("ALL");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          setImpactFilter("ALL");
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-slate-500" />
                    </div>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent className="min-w-0">
                {impactOptions.map((opt) => {
                  const count = reviewsData?.impact_counts?.[opt.value] || 0;

                  return (
                    <SelectItem key={opt.value} value={opt.value} className="text-sm w-full cursor-pointer pr-1" hideCheckmark>
                      <div className="flex items-center">
                        <span className="w-[60px] text-left">{opt.label}</span>
                        <span className="font-bold ml-2">{count}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <span className="text-sm">{selectedImpactCount}</span>
          </div>
          <TilesBulkActions
            filteredItems={filteredItems}
            documentId={documentId}
            selectedQueryTerms={selectedQueryTerms}
            onOperationStateChange={setIsBulkOperationInProgress}
            impactFilter={impactFilter}
          />
        </div>
      )}
      <div className="flex flex-col gap-3 w-full flex-1 overflow-y-auto">
        <React.Suspense fallback={<Loader className="animate-spin self-center" />}>
          {filteredItems.map((item) => (
            <TileItem
              key={item.id}
              item={item}
              documentId={documentId}
              isBulkOperationInProgress={isBulkOperationInProgress}
              onTileClick={handleTileClick}
            />
          ))}
        </React.Suspense>
      </div>

      {/* Rate Response Dialog */}
      {/* The RatingDialog is now handled within TileActions */}
    </div>
  );
};

export default TilesList;
